import React from 'react';
import { useTranslation } from 'react-i18next';
import './ProposalMessage.css';

interface ProposalData {
  projectId: number;
  projectTitle: string;
  coverLetter: string;
  proposedBudget: number | null;
  proposedDuration: number | null;
  originalBudgetMin: number | null;
  originalBudgetMax: number | null;
  createdAt: string;
}

interface ProposalMessageProps {
  messageId: number;
  proposalData: ProposalData;
  proposalStatus: 'pending' | 'accepted' | 'rejected' | 'withdrawn';
  isMyMessage: boolean;
  isClient: boolean;
  onAccept?: (messageId: number) => void;
  onReject?: (messageId: number) => void;
  onStartContract?: (messageId: number, proposalData: ProposalData) => void;
}

const ProposalMessage: React.FC<ProposalMessageProps> = ({
  messageId,
  proposalData,
  proposalStatus,
  isMyMessage,
  isClient,
  onAccept,
  onReject,
  onStartContract
}) => {
  const { t } = useTranslation();
  const getStatusBadge = () => {
    switch (proposalStatus) {
      case 'accepted':
        return <span className="status-badge accepted">✅ {t('chat.accepted')}</span>;
      case 'rejected':
        return <span className="status-badge rejected">❌ {t('chat.rejected')}</span>;
      case 'withdrawn':
        return <span className="status-badge withdrawn">🔙 {t('proposalMsg.withdrawn')}</span>;
      default:
        return <span className="status-badge pending">⏳ {t('proposalMsg.reviewing')}</span>;
    }
  };

  const formatBudget = (amount: number | null) => {
    if (!amount) return t('projects.negotiable');
    return t('projects.budgetExact', { v: amount.toLocaleString() });
  };

  return (
    <div className={`proposal-message ${isMyMessage ? 'my-proposal' : 'other-proposal'}`}>
      <div className="proposal-header">
        <span className="proposal-icon">📋</span>
        <span className="proposal-title">{t('proposalMsg.title')}</span>
        {getStatusBadge()}
      </div>

      <div className="proposal-content">
        <div className="proposal-project">
          <strong>{t('proposalMsg.project')}</strong> {proposalData.projectTitle}
        </div>

        <div className="proposal-details">
          <div className="detail-item">
            <span className="label">{t('proposalMsg.proposedBudget')}</span>
            <span className="value">{formatBudget(proposalData.proposedBudget)}</span>
          </div>
          {proposalData.proposedDuration && (
            <div className="detail-item">
              <span className="label">{t('projectDetail.duration')}</span>
              <span className="value">{t('projectDetail.daysN', { n: proposalData.proposedDuration })}</span>
            </div>
          )}
        </div>

        <div className="proposal-cover-letter">
          <strong>{t('proposalMsg.coverLetter')}</strong>
          <p>{proposalData.coverLetter.length > 200 
            ? proposalData.coverLetter.substring(0, 200) + '...' 
            : proposalData.coverLetter}
          </p>
        </div>
      </div>

      {/* 의뢰자이고 대기 중인 제안서인 경우 액션 버튼 표시 */}
      {isClient && proposalStatus === 'pending' && !isMyMessage && (
        <div className="proposal-actions">
          <button 
            className="btn-reject" 
            onClick={() => onReject && onReject(messageId)}
          >
            {t('common.reject')}
          </button>
          <button 
            className="btn-accept" 
            onClick={() => onAccept && onAccept(messageId)}
          >
            {t('proposalMsg.accept')}
          </button>
        </div>
      )}

      {/* 수락된 제안서이고 의뢰자가 아닌 경우(전문가) 계약서 작성 버튼 */}
      {!isClient && proposalStatus === 'accepted' && isMyMessage && (
        <div className="proposal-actions">
          <button 
            className="btn-contract" 
            onClick={() => onStartContract && onStartContract(messageId, proposalData)}
          >
            📝 {t('contractModal.title')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProposalMessage;
